import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser';
import { devLog } from '../utils';

const WRITE_KEY = import.meta.env.VITE_CUSTOMER_IO_WRITE_KEY;
const CDN_URL = import.meta.env.VITE_CUSTOMER_IO_CDN_URL;
const SITE_ID = import.meta.env.VITE_CUSTOMER_IO_SITE_ID;

/** Journey trigger — use this event name in Customer.io campaigns that need an explicit “login”. */
export const CUSTOMER_IO_EVENT_PARTNER_PORTAL_LOGIN = 'partner_portal_login';

/** Explicit survey response for dashboards that need user_id + venue_id on the event payload. */
export const CUSTOMER_IO_EVENT_DEALS_EASE_SURVEY_RESPONSE = 'deals_ease_survey_response';
export const CUSTOMER_IO_EVENT_IN_APP_MESSAGE_OPENED = 'in_app_message_opened';
export const CUSTOMER_IO_EVENT_IN_APP_MESSAGE_DISMISSED = 'in_app_message_dismissed';
export const CUSTOMER_IO_EVENT_IN_APP_MESSAGE_CHANGED = 'in_app_message_changed';
export const CUSTOMER_IO_EVENT_IN_APP_MESSAGE_ERROR = 'in_app_message_error';

const IN_APP_PLUGIN = 'Customer.io In-App Plugin';

function attachCustomerIoDebugApi(CustomerIOClass) {
  const allowDebug = !import.meta.env.VITE_MODE || import.meta.env.VITE_MODE === 'development';
  if (!allowDebug || typeof window === 'undefined') {
    return;
  }
  window.__EC_CUSTOMER_IO_DEBUG = {
    getStatus: () => ({
      stepGuide: '[1/5] initialize · [2/5] setUserType · [3/5] identify · [4/5] page · [5/5] reset',
      hasWriteKey: Boolean(WRITE_KEY),
      enableCustomerIo: import.meta.env.VITE_ENABLE_CUSTOMER_IO === 'true',
      userType: CustomerIOClass.userType,
      sdkInstance: Boolean(CustomerIOClass.analytics),
      lastIdentifyKey: CustomerIOClass.lastIdentifyKey,
      lastPartnerUserId: CustomerIOClass.lastPartnerUserId,
      lastVenueId: CustomerIOClass.lastVenueId,
    }),
  };
  devLog(
    'info',
    'customerio',
    'Tip: run __EC_CUSTOMER_IO_DEBUG.getStatus() anytime for a live validation snapshot.',
  );
}

class CustomerIO {
  static setUserType(userType) {
    this.userType = userType;
    devLog('info', 'customerio', '[2/5] setUserType', { userType });
  }

  static initialize() {
    devLog('info', 'customerio', '[1/5] initialize — config snapshot', {
      hasWriteKey: Boolean(WRITE_KEY),
      writeKeySuffix: WRITE_KEY ? `…${WRITE_KEY.slice(-4)}` : null,
      enableFlag: import.meta.env.VITE_ENABLE_CUSTOMER_IO,
      cdnURL: CDN_URL || '(default)',
      hasSiteId: Boolean(SITE_ID),
    });

    if (!WRITE_KEY) {
      devLog('warn', 'customerio', '[1/5] SDK not loaded: missing VITE_CUSTOMER_IO_WRITE_KEY');
      attachCustomerIoDebugApi(CustomerIO);
      return;
    }

    try {
      const settings = { writeKey: WRITE_KEY };
      if (CDN_URL) {
        settings.cdnURL = CDN_URL;
      }
      const initOptions = {
        integrations: {
          [IN_APP_PLUGIN]: {
            ...(SITE_ID ? { siteId: SITE_ID } : {}),
            events: CustomerIO.handleInAppIntegrationEvent,
          },
        },
      };
      this.analytics = AnalyticsBrowser.load(settings, initOptions);
      devLog(
        'success',
        'customerio',
        '[1/5] AnalyticsBrowser.load() OK (in-app events + CDP; buffered until ready)',
      );
    } catch (error) {
      devLog('error', 'customerio', '[1/5] AnalyticsBrowser.load() failed', error);
    }

    attachCustomerIoDebugApi(CustomerIO);
  }

  static identifyPartner(userInfo, restaurant) {
    const label = '[3/5] identify';

    if (import.meta.env.VITE_ENABLE_CUSTOMER_IO !== 'true') {
      devLog('warn', 'customerio', `${label} SKIP — set VITE_ENABLE_CUSTOMER_IO=true to send data`);
      return;
    }

    if (!WRITE_KEY || !this.analytics) {
      devLog('warn', 'customerio', `${label} SKIP — no write key or SDK not initialized`);
      return;
    }

    if (!this.userType || this.userType === 'staff') {
      devLog(
        'warn',
        'customerio',
        `${label} SKIP — Customer.io disabled for staff or unknown userType`,
        {
          userType: this.userType,
        },
      );
      return;
    }

    if (userInfo?.userType !== 'partner') {
      devLog('info', 'customerio', `${label} SKIP — not a partner session`);
      return;
    }

    if (!userInfo.objectId || !userInfo.email) {
      devLog('warn', 'customerio', `${label} SKIP — waiting for user objectId / email from API`);
      return;
    }

    if (!restaurant?.objectId) {
      devLog('warn', 'customerio', `${label} SKIP — no active venue yet`);
      return;
    }

    const dedupeKey = `${userInfo.objectId}:${restaurant.objectId}`;
    if (this.lastIdentifyKey === dedupeKey) {
      devLog('info', 'customerio', `${label} SKIP — already sent for this user+venue (deduped)`);
      return;
    }

    try {
      const traits = {
        email: userInfo.email,
        user_type: userInfo.userType,
        venue_id: restaurant.objectId,
        venue_name: restaurant.name,
        venue_region: restaurant.region,
        venue_country_code: restaurant.countryCode,
      };
      this.analytics.identify(userInfo.objectId, traits);
      this.lastIdentifyKey = dedupeKey;
      this.lastPartnerUserId = userInfo.objectId;
      this.lastVenueId = restaurant.objectId;

      this.analytics
        .track(CUSTOMER_IO_EVENT_PARTNER_PORTAL_LOGIN, {
          user_id: userInfo.objectId,
          venue_id: restaurant.objectId,
        })
        .catch((err) =>
          devLog(
            'error',
            'customerio',
            `${CUSTOMER_IO_EVENT_PARTNER_PORTAL_LOGIN} track failed`,
            err,
          ),
        );
      devLog(
        'tracking',
        'customerio',
        `${label} SENT + ${CUSTOMER_IO_EVENT_PARTNER_PORTAL_LOGIN}`,
        {
          userId: userInfo.objectId,
          venueId: restaurant.objectId,
          traitsForConsole: {
            ...traits,
            email: '(set on profile — redacted in logs)',
          },
        },
      );
    } catch (error) {
      devLog('error', 'customerio', `${label} FAILED`, error);
    }
  }

  static page(pathWithSearch) {
    const label = '[4/5] page';

    if (import.meta.env.VITE_ENABLE_CUSTOMER_IO !== 'true') {
      devLog('warn', 'customerio', `${label} SKIP — VITE_ENABLE_CUSTOMER_IO !== 'true'`, {
        path: pathWithSearch,
      });
      return;
    }

    if (!WRITE_KEY || !this.analytics) {
      devLog('warn', 'customerio', `${label} SKIP — no write key or SDK`, { path: pathWithSearch });
      return;
    }

    if (!this.userType || this.userType === 'staff') {
      if (!this.userType) {
        devLog('info', 'customerio', `${label} SKIP — not signed in yet`, { path: pathWithSearch });
      } else {
        devLog('warn', 'customerio', `${label} SKIP — staff session`, {
          path: pathWithSearch,
          userType: this.userType,
        });
      }
      return;
    }

    try {
      const path = pathWithSearch || '/';
      const href = typeof window !== 'undefined' ? window.location.href : path;
      this.analytics.page(null, path, { path, url: href });
      devLog('tracking', 'customerio', `${label} SENT`, { path });
    } catch (error) {
      devLog('error', 'customerio', `${label} FAILED`, error);
    }
  }

  static reset() {
    const label = '[5/5] reset';

    if (import.meta.env.VITE_ENABLE_CUSTOMER_IO !== 'true') {
      devLog('warn', 'customerio', `${label} SKIP — VITE_ENABLE_CUSTOMER_IO !== 'true'`);
      return;
    }

    if (!this.analytics) {
      devLog('warn', 'customerio', `${label} SKIP — SDK not initialized`);
      return;
    }

    if (!this.userType || this.userType === 'staff') {
      devLog('warn', 'customerio', `${label} SKIP — staff or userType unset (nothing to clear)`);
      return;
    }

    try {
      this.lastIdentifyKey = null;
      this.lastPartnerUserId = null;
      this.lastVenueId = null;
      this.analytics.reset();
      devLog('tracking', 'customerio', `${label} SENT — anonymous session restored`);
    } catch (error) {
      devLog('error', 'customerio', `${label} FAILED`, error);
    }
  }

  /**
   * CustomEvents from Customer.io In-App (Gist); merged with CDN integration settings.
   * @see https://docs.customer.io/integrations/data-in/connections/javascript/in-app/#listen-to-in-app-message-events
   */
  static handleInAppIntegrationEvent(event) {
    if (!event || !event.type) {
      return;
    }
    if (import.meta.env.VITE_ENABLE_CUSTOMER_IO !== 'true') {
      return;
    }
    const analytics = CustomerIO.analytics;
    if (!analytics) {
      return;
    }
    const detail = event.detail || {};
    const actionLabel =
      detail.actionName ?? detail.name ?? detail.actionValue ?? detail.action ?? '';
    const userId = CustomerIO.lastPartnerUserId;
    const venueId = CustomerIO.lastVenueId;

    // Always log raw in-app events so campaign/survey delivery can be debugged quickly in dev.
    devLog('info', 'customerio', `[in-app] ${event.type}`, {
      messageId: detail.messageId || '',
      deliveryId: detail.deliveryId || '',
      actionName: detail.actionName || '',
      actionValue: detail.actionValue || '',
    });

    const inAppEventMap = {
      'in-app:message-opened': CUSTOMER_IO_EVENT_IN_APP_MESSAGE_OPENED,
      'in-app:message-dismissed': CUSTOMER_IO_EVENT_IN_APP_MESSAGE_DISMISSED,
      'in-app:message-changed': CUSTOMER_IO_EVENT_IN_APP_MESSAGE_CHANGED,
      'in-app:message-error': CUSTOMER_IO_EVENT_IN_APP_MESSAGE_ERROR,
    };

    const mappedEventName = inAppEventMap[event.type];
    if (mappedEventName && userId) {
      analytics
        .track(mappedEventName, {
          user_id: userId,
          venue_id: venueId || '',
          message_id: detail.messageId || '',
          delivery_id: detail.deliveryId || '',
          action_name: detail.actionName || '',
          action_value: detail.actionValue || '',
        })
        .catch((err) => devLog('error', 'customerio', `${mappedEventName} track failed`, err));
    }

    if (event.type !== 'in-app:message-action') {
      return;
    }

    if (!userId) {
      devLog(
        'warn',
        'customerio',
        'in-app:message-action — no lastPartnerUserId; skipping deals_ease_survey_response',
      );
      return;
    }
    analytics
      .track(CUSTOMER_IO_EVENT_DEALS_EASE_SURVEY_RESPONSE, {
        user_id: userId,
        venue_id: venueId || '',
        response: String(actionLabel),
        message_id: detail.messageId || '',
        delivery_id: detail.deliveryId || '',
      })
      .catch((err) =>
        devLog(
          'error',
          'customerio',
          `${CUSTOMER_IO_EVENT_DEALS_EASE_SURVEY_RESPONSE} track failed`,
          err,
        ),
      );
    devLog('tracking', 'customerio', `${CUSTOMER_IO_EVENT_DEALS_EASE_SURVEY_RESPONSE} queued`, {
      user_id: userId,
      venue_id: venueId,
      response: String(actionLabel),
      message_id: detail.messageId || '',
      delivery_id: detail.deliveryId || '',
    });
  }
}

CustomerIO.userType = null;
CustomerIO.analytics = null;
/** Dedupe React Strict Mode / repeated effect runs for the same user + venue */
CustomerIO.lastIdentifyKey = null;
CustomerIO.lastPartnerUserId = null;
CustomerIO.lastVenueId = null;

export default CustomerIO;
